import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Searchable single-select combobox for Item lists. Filters by SKU + name
 * as the user types — designed for recipes/deliveries/orders that
 * reference 50+ raw materials where a plain <Select> becomes unusable.
 *
 * Props:
 *   items:        [{id, sku, name, unit}]
 *   value:        currently selected item id (string) or ""
 *   onChange:     (newId) => void
 *   placeholder:  text shown when nothing is selected
 *   testId:       optional data-testid prefix (`${testId}-trigger`)
 *   emptyLabel:   text when the search returns no hits
 *   onCreate:     optional (searchTerm) => void — enables a
 *                 "➕ Neuen Artikel anlegen"-Footer that hands the current
 *                 search string to the caller so it can pre-fill a
 *                 create-dialog.
 *   createLabel:  custom footer text (default: "Neuen Artikel anlegen")
 */
export default function ItemCombobox({
  items = [], value = "", onChange, placeholder = "Rohstoff wählen…",
  testId, emptyLabel = "Kein Treffer.",
  onCreate, createLabel = "Neuen Artikel anlegen",
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const selected = items.find((i) => i.id === value);
  const triggerCreate = () => {
    if (!onCreate) return;
    onCreate(search.trim());
    setOpen(false);
    setSearch("");
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId ? `${testId}-trigger` : undefined}
          className={cn(
            "w-full justify-between h-10 font-normal",
            !selected && "text-[var(--text-muted)]",
          )}
        >
          <span className="truncate flex items-baseline gap-2 min-w-0">
            {selected ? (
              <>
                {selected.sku && (
                  <span className="font-mono text-[11px] text-[var(--text-muted)] shrink-0">{selected.sku}</span>
                )}
                <span className="truncate">{selected.name}</span>
              </>
            ) : placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(560px,90vw)]" align="start">
        <Command
          filter={(itemValue, s) => {
            if (!s) return 1;
            const q = s.trim().toLowerCase();
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Suchen (SKU oder Name)…"
                        value={search}
                        onValueChange={setSearch}
                        data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>
              <div className="py-4 text-sm text-[var(--text-muted)]">{emptyLabel}</div>
            </CommandEmpty>
            {(() => {
              // Group by item.type so recipes can visually distinguish
              // Rohstoffe from Verpackungen in the same picker.
              const groups = { raw: [], packaging: [], other: [] };
              for (const i of items) {
                const t = (i.type || "raw").toLowerCase();
                if (t === "packaging") groups.packaging.push(i);
                else if (t === "raw") groups.raw.push(i);
                else groups.other.push(i);
              }
              const sections = [
                { key: "raw", label: "Rohstoffe", list: groups.raw },
                { key: "packaging", label: "Verpackungen", list: groups.packaging },
                { key: "other", label: "Sonstige", list: groups.other },
              ].filter((s) => s.list.length > 0);
              return sections.map((sec) => (
                <CommandGroup key={sec.key} heading={sec.label}>
                  {sec.list.map((i) => {
                    const key = `${i.sku || ""}##${i.name}##${i.id}`;
                    const isPack = (i.type || "").toLowerCase() === "packaging";
                    return (
                      <CommandItem
                        key={i.id}
                        value={key}
                        data-testid={testId ? `${testId}-option-${i.id}` : undefined}
                        onSelect={() => { onChange(i.id); setOpen(false); setSearch(""); }}
                        className="cursor-pointer"
                      >
                        <Check size={14} className={cn("mr-2", value === i.id ? "opacity-100" : "opacity-0")} />
                        {i.sku && <span className="font-mono text-[11px] text-[var(--text-muted)] mr-2">{i.sku}</span>}
                        <span className="truncate">{i.name}</span>
                        {isPack && (
                          <span className="ml-2 pop-pill bg-sky-100 text-sky-800 text-[9px] font-bold">VERP</span>
                        )}
                        {i.unit && <span className="ml-auto text-[10px] text-[var(--text-muted)]">{i.unit}</span>}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ));
            })()}
          </CommandList>
          {onCreate && (
            <div className="border-t border-[var(--border)] p-1">
              <button type="button"
                      onClick={triggerCreate}
                      data-testid={testId ? `${testId}-create` : undefined}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-left hover:bg-[var(--pop-cream)]/60 text-[var(--pop-pink)] font-semibold">
                <Plus size={14} />
                <span className="truncate">
                  {createLabel}{search.trim() ? <> · <span className="font-mono text-[11px] text-black">"{search.trim()}"</span></> : null}
                </span>
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
